import { PomodoroService } from "features/widget-workspace/service/PomodoroService";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { TimerAlarmSoundsMap } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { useUserContext } from "@contexts/UserContext";
import { addOnCloseMainWindowEventListener } from "@desktop/ToDestkopEventListeners";
import { logError } from "@service/ConsoleLoggerService";
import { updateFocusTime } from "@user/service/AllUserMetricsService";
import { RoomParams } from "../../../../../room/RoomTypes";
import { taskTimerState$ } from "../../../../../task-management/TaskTimerGlobals";
import { getAlarmSound, getTimerDisplayText } from "../PomodoroUtil";
import { DEFAULT_TASK_TIMER_MINUTES, LIFEAT_SPACES_DOC_TITLE, TIMER_DOC_TITLE_SUFFIX } from "../TimerWidgetConstants";
import { useCountdownTimer } from "./useCountdownTimer";
import { usePomodoroSettingsInput } from "./usePomodoroSettingsInput";
import { useSelectedPomodoroSound } from "./useSelectedPomodoroSound";

type Options = {
    hidden: boolean;
    pomodoroService: PomodoroService;
    alarmSoundOptions: TimerAlarmSoundsMap;
    audioRef: React.MutableRefObject<HTMLAudioElement | null>;
};

export function useTaskTimer(options: Options) {
    const { roomId } = useParams<RoomParams>();
    const { user, userData } = useUserContext();

    const { pomodoroSettingsInput, dispatchPomodoroSettingsInput, saveSettings } = usePomodoroSettingsInput(
        options.pomodoroService,
        roomId,
    );
    useSelectedPomodoroSound(options.pomodoroService, pomodoroSettingsInput, dispatchPomodoroSettingsInput);
    const { selectedAlarmSound } = pomodoroSettingsInput;

    const durationMins =
        (taskTimerState$.value.isTimerRunning && taskTimerState$.value.taskDuration) || DEFAULT_TASK_TIMER_MINUTES;

    const handleTimerEnd = () => {
        // if user is authenticated, update their focus time metric
        if (user && userData) {
            updateFocusTime(durationMins);
        }
        countdownTimer.reset(false);
        taskTimerState$.value = { isTimerRunning: false };
    };

    // This handles clearing the task timer state when the desktop app is closed
    // so that the task timer doesn't auto start if you close and re-open the app when the timer is running
    useEffect(() => {
        if (window.todesktop) {
            const unsubscribe = addOnCloseMainWindowEventListener(
                () => (taskTimerState$.value = { isTimerRunning: false }),
            );
            return () => unsubscribe?.();
        }
        return undefined;
    }, [taskTimerState$.value]);

    const handleAlarm = () => {
        if (options.audioRef.current == null) {
            logError(`Audio Ref in useHeadfulPomodoro is null!`);
        } else {
            options.audioRef.current?.play().catch((e) => logError("Error playing pomodoro alarm audio: " + e));
        }
    };

    const countdownTimer = useCountdownTimer({
        duration: durationMins * 60 * 1000,
        onTimerEnd: handleTimerEnd,
        onAlarm: handleAlarm,
        alarmRingOffset: getAlarmSound(options.alarmSoundOptions, selectedAlarmSound).soundDuration,
    });

    const start = () => {
        if (options.audioRef.current == null) {
            logError(`audioRef is null, cannot load()`);
        }
        // Note: load audio on user interaction to handle blocked autoplay on safari mobile
        options.audioRef.current?.load();
        countdownTimer.start();
    };

    const pause = () => {
        countdownTimer.pause();
    };

    const reset = () => {
        countdownTimer.reset(false);
    };

    // Pause & reset timer when configuration changes.
    useEffect(() => {
        countdownTimer.reset(false);
    }, [durationMins]);

    const timerDisplayText = getTimerDisplayText(countdownTimer.state.currMillis);
    const isRunning = !!countdownTimer.state.timer;

    useEffect(() => {
        if (!options.hidden) {
            if (isRunning) {
                document.title = `${timerDisplayText}${TIMER_DOC_TITLE_SUFFIX}`;
            } else {
                document.title = LIFEAT_SPACES_DOC_TITLE;
            }
        }
    }, [isRunning, timerDisplayText, options.hidden]);

    return {
        saveSettings,
        start,
        pause,
        reset,
        isRunning,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
    };
}
